import { createTypes, createActions, createReducer, createSelector, assignLeft } from './util'
import uuid from 'uuid'
import _ from 'lodash/fp'

const namespace = 'shots'

const typeKeys = [
  'CLEAR',
  'ASSIGN',
  'MERGE',
  'ADJUST_SHOTS'
]

export const types = createTypes(typeKeys, namespace)

export const actions = createActions(types)

const initialState = {
  // gameId -> { holeId -> [{ shotId }, { shotId }, { shotId }] }
}

/**
 * a [{ shotId }, { shotId }, { shotId }]
 * b [{ shotId }, { shotId }, { shotId }]
 */
const mergeArrays = b => _.flow(
  _.values,
  _.concat(_.values(b)),
  _.groupBy('shotId'),
  _.map(([firstShot, secondShot]) => {
    // If there is only one shot in the group, then it is either new on the server or in the app
    if (!secondShot) {
      return firstShot
    }

    // If the two shots are equal, then return one
    if (_.isEqual(_.get('deletedAt', firstShot), _.get('deletedAt', secondShot))) {
      return _.omit('d', firstShot)
    }

    // Else return a shot with max deletedAt and d: 1
    return assignLeft({ d: 1, deletedAt: _.max([_.get('deletedAt', firstShot), _.get('deletedAt', secondShot)]) }, firstShot)
  }),
  _.keyBy('shotId')
)

/**
 * a holeId -> [{ shotId }, { shotId }, { shotId }]
 * b holeId -> [{ shotId }, { shotId }, { shotId }]
 */
const mergeShotMaps = shotMaps => _.reduce.convert({ cap: false })(
  (acc, shotMap, holeId) => _.update(`${holeId}`, mergeArrays(shotMap), acc),
  shotMaps
)

const reducer = {
  [types.CLEAR]: p => _.constant(initialState),

  [types.ASSIGN]: assignLeft,

  [types.MERGE]: games => state => {
    return _.flow(
      _.keyBy('gameId'),
      _.mapValues(
        _.flow(_.get('holesShots'), _.groupBy('holeId'))
      ),
      _.reduce.convert({ cap: false })(
        (acc, shotMaps, gameId) => _.update(gameId, mergeShotMaps(shotMaps), acc),
        state
      )
    )(games)
  },

  [types.ADJUST_SHOTS]: payload => state => {
    const gameId = _.get('gameId', payload)
    const holeId = _.get('holeId', payload)
    const adjustShotsDiff = _.get('diff', payload)
    if (!gameId || !holeId || adjustShotsDiff === 0) {
      return state
    }
    if (adjustShotsDiff > 0) {
      const createdAt = Date.now()
      const setters = _.map(
        v => {
          const shotId = uuid.v4()
          const updatePath = `${gameId}.${holeId}.${shotId}`
          return _.set(updatePath, { shotId, createdAt, d: 1, deletedAt: null, holeId: _.toNumber(holeId) })
        },
        _.range(0, adjustShotsDiff)
      )
      return _.flow(setters)(state)
    }
    // adjustShotsDiff < 0
    const deletedAt = Date.now()
    const shotIds = _.flow(
      _.get(`${gameId}.${holeId}`),
      _.reject('deletedAt'),
      _.sortBy('createdAt'),
      _.reverse,
      _.map('shotId'),
      _.slice(0, Math.abs(adjustShotsDiff)))(state)
    const setters = _.map(
      shotId => _.update(`${gameId}.${holeId}.${shotId}`, assignLeft({ deletedAt, d: 1 })),
      shotIds
    )
    return _.flow(setters)(state)
  }
}

export default createReducer(initialState, reducer)

function createSelectors () {
  const getShots = _.get(`${namespace}`)
  const getShotsForGameId = gameId => _.get(`${namespace}.${gameId}`)

  const getGamesShots = createSelector(
    getShots,
    _.mapValues(
      _.mapValues(
        _.flow(
          _.reject('deletedAt'),
          _.size
        )))
  )
  const getGamesTotalShots = createSelector(
    getGamesShots,
    _.mapValues(_.flow(_.values, _.sum))
  )

  return {
    getShots,
    getShotsForGameId,
    getGamesShots,
    getGamesTotalShots
  }
}

export const selectors = createSelectors()
